import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const useUnsavedChanges = (isDirty) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [intendedPath, setIntendedPath] = useState(null);
  const previousLocation = useRef(location.pathname);

  // Handle browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = 'Are you sure you want to close this window?';
        return 'Are you sure you want to close this window?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Handle navigation confirmation for all URL changes
  useEffect(() => {
    if (isDirty && intendedPath && intendedPath !== location.pathname) {
      const confirmLeave = window.confirm('Are you sure you want to close this window?');
      if (confirmLeave) {
        previousLocation.current = intendedPath;
        navigate(intendedPath);
      } else {
        navigate(previousLocation.current, { replace: true });
        setIntendedPath(null);
      }
    } else if (intendedPath) {
      previousLocation.current = intendedPath;
      navigate(intendedPath);
    }
  }, [intendedPath, isDirty, navigate, location.pathname]);

  // Detect browser back/forward navigation (URL changes via browser buttons)
useEffect(() => {
  const handlePopstate = () => {
    if (isDirty && location.pathname !== previousLocation.current) {
      setIntendedPath(location.pathname);
    } else {
      previousLocation.current = location.pathname;
    }
  };

  window.addEventListener('popstate', handlePopstate);
  return () => window.removeEventListener('popstate', handlePopstate);
}, [isDirty, location.pathname]);


  // Function for all programmatic navigation (buttons, links, etc.)
  const confirmNavigation = (path) => {
    if (isDirty && path !== location.pathname) {
      setIntendedPath(path);
    } else {
      previousLocation.current = path;
      navigate(path);
    }
  };

  return { confirmNavigation };
};

export default useUnsavedChanges;